import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { fetchWithAuth } from '../utils/apiUtils';
import { toast } from 'react-toastify';
import { API_URL } from '../config';
import { useLocation } from 'react-router-dom';

// Create the context
const AdminContext = createContext();

// Custom hook for using the Admin context
export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

// Admin Provider component
export const AdminProvider = ({ children }) => {
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // Helper function to determine if we're in sub-admin mode
  const isSubAdminRoute = () => {
    return location.pathname.startsWith('/subadmin');
  };
  
  // Helper function to get the correct API base path
  const getApiBasePath = () => {
    return isSubAdminRoute() ? 'subadmin' : 'admin';
  };
  const [users, setUsers] = useState([]);
  const [subscriptionUsers, setSubscriptionUsers] = useState([]);
  const [workoutPlans, setWorkoutPlans] = useState([]);
  const [dietPlans, setDietPlans] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [latestSubscriptions, setLatestSubscriptions] = useState([]);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  // Add pagination state to store across component mounts
  const [usersPagination, setUsersPagination] = useState({
    skip: 0,
    limit: 10,
    total: 0
  });
  
  const isCheckingAdminRef = useRef(false);
  const checkCancelledRef = useRef(false);
  const initialCheckDoneRef = useRef(false);
  const lastFetchUsersParamsRef = useRef(null);
  const fetchingUsersRef = useRef(false);

  // Use the centralized configuration
  const API_BASE_URL = API_URL;

  // Set initial admin status from localStorage on mount
  useEffect(() => {
    // Only run once on mount
    if (!initialCheckDoneRef.current) {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          console.log('🔍 AdminContext: Setting initial admin status for user type:', user.user_type);
          if (user.user_type === 'admin' || user.user_type === 'sub_admin') {
            console.log('✅ AdminContext: Setting isAdmin to true for', user.user_type);
            setIsAdmin(true);
          } else {
            console.log('❌ AdminContext: Setting isAdmin to false for', user.user_type);
            setIsAdmin(false);
          }
        } else {
          setIsAdmin(false);
        }
      } catch (err) {
        console.error("Error setting initial admin status:", err);
        setIsAdmin(false);
      }
      initialCheckDoneRef.current = true;
    }
  }, []);

  // Check if the current user is an admin
  useEffect(() => {
    if (isCheckingAdminRef.current){
      return;
    }

    const checkAdminStatus = async () => {
      // Skip if we've already verified admin status or if we know they're not an admin/sub-admin
      if (dashboardStats || (initialCheckDoneRef.current && !isAdmin && !isSubAdminRoute())) {
        console.log('⏭️ AdminContext: Skipping admin status check - already done or not admin/sub-admin');
        return;
      }

      // Set flag to avoid duplicate checks
      isCheckingAdminRef.current = true;
      checkCancelledRef.current = false;

      try {
        setIsLoading(true);
        
        // First get user from localStorage
        let user;
        try {
          const userJson = localStorage.getItem('user');
          if (userJson) {
            user = JSON.parse(userJson);
            
            // Check if user_type is 'admin' or 'sub_admin'
            console.log('🔍 AdminContext: Checking user type:', user.user_type);
            if (user.user_type !== 'admin' && user.user_type !== 'sub_admin') {
              console.log('❌ AdminContext: User is not admin or sub-admin, rejecting');
              setIsAdmin(false);
              setIsLoading(false);
              isCheckingAdminRef.current = false;
              return;
            }
            console.log('✅ AdminContext: User is admin or sub-admin, proceeding');
          } else {
            setIsAdmin(false);
            setIsLoading(false);
            isCheckingAdminRef.current = false;
            return;
          }
        } catch (err) {
          console.error("Error parsing user from localStorage:", err);
          setIsAdmin(false);
          setIsLoading(false);
          isCheckingAdminRef.current = false;
          return;
        }
        
        // User is an admin type, verify with API call
        try {
          // Skip API call if component is unmounting
          if (checkCancelledRef.current) {
            isCheckingAdminRef.current = false;
            setIsLoading(false);
            return;
          }

        // Try a simple admin check endpoint
        console.log('🔍 AdminContext: Making API call to:', `${API_BASE_URL}/${getApiBasePath()}/dashboard-stats`);
        const response = await fetchWithAuth(`${API_BASE_URL}/${getApiBasePath()}/dashboard-stats`);
          
          // Skip state update if component is unmounting - but ignore for debugging
          console.log('🔍 AdminContext: checkCancelledRef.current =', checkCancelledRef.current);
          // if (checkCancelledRef.current) {
          //   isCheckingAdminRef.current = false;
          //   return;
          // }
         
          if (!response.error && response.data) {
            console.log('✅ AdminContext: API call successful, setting admin status and dashboard stats');
            console.log('📊 Dashboard stats received:', response.data);
            setIsAdmin(true);
            setDashboardStats(response.data);
            return; // Exit early after successful admin verification
          } else {
            console.error('❌ AdminContext: Failed to verify admin status:', response);
            toast.error('Admin verification failed. Please log in again.');
            setIsAdmin(false);
          }
        } catch (err) {
          console.error('Error validating admin status:', err);
          toast.error(`Admin validation error: ${err.message}`);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        toast.error(`Admin verification error: ${error.message}`);
        setIsAdmin(false);
      } finally {
        // Reset flag after completion
        isCheckingAdminRef.current = false;
        setIsLoading(false);
      }
    };

    // Check admin status if we haven't already verified and user is supposed to be admin or sub-admin
    // Also check if we're in a sub-admin route (to handle initial load)
    const shouldCheckAdminStatus = !dashboardStats && !isCheckingAdminRef.current && (isAdmin || isSubAdminRoute());
    
    console.log('🔍 AdminContext: Should check admin status?', {
      shouldCheck: shouldCheckAdminStatus,
      hasDashboardStats: !!dashboardStats,
      isChecking: isCheckingAdminRef.current,
      isAdmin,
      isSubAdminRoute: isSubAdminRoute()
    });
    
    if (shouldCheckAdminStatus) {
      console.log('🚀 AdminContext: Starting admin status check...');
      checkAdminStatus();
    }

    // Cleanup function
    return () => {
      // Mark any in-progress check as cancelled
      checkCancelledRef.current = true;
    };
  }, [isAdmin, dashboardStats]);

  // Ensure loading is set to false after initial mount if no admin check is needed
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isCheckingAdminRef.current) {
        console.log('Setting isLoading to false - no admin check in progress');
        setIsLoading(false);
      }
    }, 1000); // Give 1 second for any initial checks to complete
    
    return () => clearTimeout(timer);
  }, []);

  // Additional safety net for loading state
  useEffect(() => {
    if (dashboardStats && isAdmin) {
      console.log('Dashboard stats loaded, ensuring isLoading is false');
      setIsLoading(false);
    }
  }, [dashboardStats, isAdmin]);

  //function for updating user
  const updateUser = async (userId, data) => {
    try {
      // Use different endpoint patterns for admin vs sub-admin
      const endpoint = isSubAdminRoute() ? 'update-user' : 'users';
      let url = `${API_BASE_URL}/${getApiBasePath()}/${endpoint}/${userId}?`;
      
      // Add each parameter to the URL if it exists
      if (data.subscription_type !== undefined) {
        url += `subscription_type=${encodeURIComponent(data.subscription_type)}&`;
      }
      
      if (data.tokens_left !== undefined) {
        url += `tokens_left=${encodeURIComponent(data.tokens_left)}&`;
      }
      
      if (data.expiry_date !== undefined) {
        url += `expiry_date=${encodeURIComponent(data.expiry_date)}&`;
      }
      
      if (data.user_type !== undefined) {
        url += `user_type=${encodeURIComponent(data.user_type)}&`;
      }
      
      // Remove trailing & if exists
      url = url.endsWith('&') ? url.slice(0, -1) : url;
      
      // console.log('Sending update request to:', url);
      
      const response = await fetchWithAuth(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  };
  

  // API function to fetch dashboard stats
  const fetchDashboardStats = async (forceRefresh = false) => {
    try {
      console.log('🔍 fetchDashboardStats called with forceRefresh:', forceRefresh);
      console.log('📊 Current dashboardStats:', dashboardStats);
      console.log('🎯 Current location.pathname:', location.pathname);
      console.log('🎯 isSubAdminRoute():', isSubAdminRoute());
      console.log('🎯 getApiBasePath():', getApiBasePath());
      
      // ALWAYS fetch data for debugging - ignore cache temporarily
      console.log('🌐 Making API call to fetch dashboard stats (force refresh for debugging)...');
      setIsLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/${getApiBasePath()}/dashboard-stats`);
            
      console.log('📡 fetchDashboardStats response:', response);
      
      if (!response.error) {
        console.log('✅ fetchDashboardStats: API call successful, response data:', response.data);
        // Always update state for debugging - ignore checkCancelledRef temporarily
        console.log('📊 fetchDashboardStats: Setting dashboardStats state (ignoring checkCancelledRef for debugging)');
        setDashboardStats(response.data);
        return response.data;
      } else {
        console.error('❌ fetchDashboardStats: API call failed:', response);
        throw new Error('Failed to fetch dashboard stats');
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    } finally {
      // Always set loading to false for debugging
      console.log('🏁 fetchDashboardStats: Setting isLoading to false');
      setIsLoading(false);
    }
  };

  // API function to fetch users with pagination and search
  const fetchUsers = async (skip = 0, limit = 10, search = null, subscription_type = null, status = null, user_type = null) => {
    try {
      // Create a params key to compare with last request
      const paramsKey = JSON.stringify({ skip, limit, search, subscription_type, status, user_type });
      
      // Check if we're already fetching or if this is a duplicate call with same params
      if (fetchingUsersRef.current) {
        // Instead of returning cached data, wait for the first call to complete
        let waitAttempts = 0;
        const maxWaitAttempts = 10;
        const waitTime = 100; // ms

        // Wait for the existing fetch to complete
        while (fetchingUsersRef.current && waitAttempts < maxWaitAttempts) {
          await new Promise(resolve => setTimeout(resolve, waitTime));
          waitAttempts++;
        }

        // If we have users data now, return it
        if (users.length > 0) {
          return { 
            users: users, 
            total: usersPagination.total 
          };
        }
      }
      
      setIsLoading(true);
      fetchingUsersRef.current = true;
      
      // Use the appropriate endpoint based on whether search/filters are provided
      let url;
      const hasFilters = search || subscription_type || status || user_type;
      if (hasFilters) {
        // Use the search endpoint if search term or filters are provided
        url = `${API_BASE_URL}/${getApiBasePath()}/users/search?skip=${skip}&limit=${limit}`;
        
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        
        if (subscription_type) {
          url += `&subscription_type=${encodeURIComponent(subscription_type)}`;
        }
        
        if (status) {
          url += `&status=${encodeURIComponent(status)}`;
        }
        
        if (user_type) {
          url += `&user_type=${encodeURIComponent(user_type)}`;
        }
      } else {
        // Use the basic users endpoint if no search term or filters
        url = `${API_BASE_URL}/${getApiBasePath()}/users?skip=${skip}&limit=${limit}`;
      }
      
      const response = await fetchWithAuth(url);
      
      // Create our return data object
      let returnData = { users: [], total: 0 };
      
      if (!response.error) {
        // Handle different response formats
        if (response.data && response.data.users) {
          returnData = {
            users: response.data.users || [],
            total: response.data.total || response.data.users.length || 0
          };
        } else if (response.users) {
          // Direct users array in response
          returnData = {
            users: response.users || [],
            total: response.total || response.users.length || 0
          };
        } else if (Array.isArray(response.data)) {
          // Handle case where data itself is the users array
          returnData = {
            users: response.data,
            total: response.total || response.data.length
          };
        } else if (Array.isArray(response)) {
          // Handle case where response itself is the users array
          returnData = {
            users: response,
            total: response.length
          };
        } else {
          // Try to extract any array we can find
          const possibleUsersArray = Object.values(response.data || response)
            .find(value => Array.isArray(value));
            
          if (possibleUsersArray) {
            returnData = {
              users: possibleUsersArray,
              total: possibleUsersArray.length
            };
          }
        }
        
        // Ensure users array is valid
        if (!Array.isArray(returnData.users)) {
          console.error("Failed to extract users array from response:", response);
          returnData.users = [];
          returnData.total = 0;
        }  
        
        // Store the fetched users in context state
        setUsers(returnData.users);
        
        // Update pagination state in context
        setUsersPagination({
          skip: skip,
          limit: limit,
          total: returnData.total
        });
        
        // Store the params we just used
        lastFetchUsersParamsRef.current = paramsKey;
        
        // Return returnData directly without additional checks
        return returnData;
      } else {
        console.error("Error response from API:", response);
        // Even on error, return a valid structure
        return { users: [], total: 0 };
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      // Return a valid structure even when there's an error
      return { users: [], total: 0 };
    } finally {
      fetchingUsersRef.current = false;
      if (!checkCancelledRef.current) {
        setIsLoading(false);
      }
    }
  };

  // API function to fetch a specific user
  const fetchUserById = async (userId) => {
    try {
      setIsLoading(true);
      // Use different endpoint patterns for admin vs sub-admin
      const endpoint = isSubAdminRoute() ? 'user' : 'users';
      const response = await fetchWithAuth(`${API_BASE_URL}/${getApiBasePath()}/${endpoint}/${userId}`);
      if (!response.error) {
        return response.data;
      } else {
        throw new Error(`Failed to fetch user with ID ${userId}`);
      }
    } catch (error) {
      console.error(`Error fetching user with ID ${userId}:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // API function to fetch workout plans with pagination
  const fetchWorkoutPlans = async (skip = 0, limit = 10, userId = null) => {
    try {
      setIsLoading(true);
      let url = `${API_BASE_URL}/admin/workout-plans?skip=${skip}&limit=${limit}`;
      if (userId) {
        url += `&user_id=${userId}`;
      }
      const response = await fetchWithAuth(url);
      if (!response.error) {
        setWorkoutPlans(response.data.workout_plans || []);
        return response.data;
      } else {
        throw new Error('Failed to fetch workout plans');
      }
    } catch (error) {
      console.error('Error fetching workout plans:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // API function to fetch diet plans with pagination
  const fetchDietPlans = async (skip = 0, limit = 10, userId = null) => {
    try {
      setIsLoading(true);
      let url = `${API_BASE_URL}/admin/diet-plans?skip=${skip}&limit=${limit}`;
      if (userId) {
        url += `&user_id=${userId}`;
      }
      const response = await fetchWithAuth(url);
      if (!response.error) {
        setDietPlans(response.data.diet_plans || []);
        return response.data;
      } else {
        throw new Error('Failed to fetch diet plans');
      }
    } catch (error) {
      console.error('Error fetching diet plans:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // API function to fetch feedback with pagination
  const fetchFeedback = async (skip = 0, limit = 10) => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/feedback?skip=${skip}&limit=${limit}`);
      if (!response.error) {
        setFeedback(response.data.feedback || []);
        return response.data;
      } else {
        throw new Error('Failed to fetch feedback');
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // API function to fetch ratings with pagination
  const fetchRatings = async (skip = 0, limit = 10) => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/ratings?skip=${skip}&limit=${limit}`);
      if (!response.error) {
        setRatings(response.data.ratings || []);
        return response.data;
      } else {
        throw new Error('Failed to fetch ratings');
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // API function to fetch analytics data
  const fetchAnalytics = async (period = 'month') => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/${getApiBasePath()}/analytics?period=${period}`);
      if (!response.error) {
        setAnalytics(response.data);
        return response.data;
      } else {
        throw new Error('Failed to fetch analytics');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // API function to fetch latest subscriptions
  const fetchLatestSubscriptions = async (limit = 10) => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/${getApiBasePath()}/latest-subscriptions?limit=${limit}`);
      if (!response.error) {
        setLatestSubscriptions(response.data.subscriptions || []);
        return response.data;
      } else {
        throw new Error('Failed to fetch latest subscriptions');
      }
    } catch (error) {
      console.error('Error fetching latest subscriptions:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // API function to fetch subscription stats
  const fetchSubscriptionStats = async (period = 'month') => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/${getApiBasePath()}/subscription-stats?period=${period}`);
      if (!response.error) {
        setSubscriptionStats(response.data);
        return response.data;
      } else {
        throw new Error('Failed to fetch subscription stats');
      }
    } catch (error) {
      console.error('Error fetching subscription stats:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // API function to fetch subscription users with pagination and filters
  const fetchSubscriptionUsers = async (skip = 0, limit = 10, search = null, subscription_type = null, status = null) => {
    try {
      setIsLoading(true);
      
      let url = `${API_BASE_URL}/${getApiBasePath()}/subscription-users?skip=${skip}&limit=${limit}`;
      
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      if (subscription_type) {
        url += `&subscription_type=${encodeURIComponent(subscription_type)}`;
      }
      
      if (status) {
        url += `&status=${encodeURIComponent(status)}`;
      }
      
      const response = await fetchWithAuth(url);
      
      if (!response.error) {
        const data = {
          users: response.data?.users || [],
          total: response.data?.total || 0,
          skip: response.data?.skip || 0,
          limit: response.data?.limit || 10
        };
        
        setSubscriptionUsers(data.users);
        return data;
      } else {
        throw new Error(response.data?.message || 'Failed to fetch subscription users');
      }
    } catch (error) {
      console.error('Error fetching subscription users:', error);
      toast.error(`Failed to load subscription users: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // API function to fetch daily active users analytics
  const fetchDailyActiveUsers = async (period = 'month', start_date = null, end_date = null) => {
    try {
      setIsLoading(true);
      
      let url = `${API_BASE_URL}/${getApiBasePath()}/daily-active-users?period=${period}`;
      
      if (start_date) {
        url += `&start_date=${start_date}`;
      }
      
      if (end_date) {
        url += `&end_date=${end_date}`;
      }
      
      const response = await fetchWithAuth(url);
      
      if (!response.error) {
        return response.data;
      } else {
        throw new Error('Failed to fetch daily active users analytics');
      }
    } catch (error) {
      console.error('Error fetching daily active users analytics:', error);
      toast.error(`Failed to load daily active users analytics: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // API function to create a new user
  const createUser = async (userData) => {
    try {
      setIsLoading(true);
      
      // Create FormData for the API call
      const formData = new FormData();
      formData.append('name', userData.name);
      formData.append('email', userData.email);
      formData.append('subscription_type', userData.subscription_type || 'Free');
      formData.append('tokens_left', userData.tokens_left || 10);
      formData.append('user_type', userData.user_type || 'free');
      formData.append('send_welcome_email', userData.send_welcome_email !== undefined ? userData.send_welcome_email.toString() : 'true');
      
      // Add password if provided for pre-verification
      if (userData.password) {
        formData.append('password', userData.password);
      }
      
      const response = await fetchWithAuth(`${API_BASE_URL}/${getApiBasePath()}/create-user`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (!response.error) {
        toast.success('User created successfully!');
        
        // Refresh the users list to include the new user
        await fetchUsers(0, usersPagination.limit);
        
        return response.data;
      } else {
        throw new Error(response.data?.detail || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(`Failed to create user: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // API function to create a new sub-admin (Add Gym functionality)
  const createSubAdmin = async (subAdminData) => {
    try {
      setIsLoading(true);
      
      // Create FormData for the API call
      const formData = new FormData();
      formData.append('name', subAdminData.name);
      formData.append('email', subAdminData.email);
      
      // Add password if provided
      if (subAdminData.password) {
        formData.append('password', subAdminData.password);
      }
      
      formData.append('gym_name', subAdminData.gym_name);
      formData.append('gym_owner_name', subAdminData.gym_owner_name);
      formData.append('user_limit', subAdminData.user_limit || 100);
      formData.append('tokens_left', subAdminData.tokens_left || 1000);
      formData.append('subscription_type', subAdminData.subscription_type || 'Premium');
      formData.append('send_invite_email', 'true');
      
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/create-subadmin`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (!response.error) {
        toast.success('Sub-admin created successfully!');
        
        // Refresh the users list to include the new sub-admin
        await fetchUsers(0, usersPagination.limit);
        
        return response.data;
      } else {
        throw new Error(response.data?.detail || 'Failed to create sub-admin');
      }
    } catch (error) {
      console.error('Error creating sub-admin:', error);
      toast.error(`Failed to create sub-admin: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Authorization management functions
  const fetchUnauthorizedUsers = async (page = 1, limit = 20, search = '') => {
    try {
      setIsLoading(true);
      
      let url = `${API_BASE_URL}/admin/unauthorized-users?page=${page}&limit=${limit}`;
      
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      const response = await fetchWithAuth(url);
      
      if (!response.error) {
        return response.data;
      } else {
        throw new Error('Failed to fetch unauthorized users');
      }
    } catch (error) {
      console.error('Error fetching unauthorized users:', error);
      toast.error(`Failed to load unauthorized users: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const authorizeUser = async (userId) => {
    try {
      setIsLoading(true);
      
      const response = await fetchWithAuth(`${API_BASE_URL}/${getApiBasePath()}/toggle-authorization/${userId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.error) {
        toast.success(response.data?.message || 'User authorized successfully!');
        
        // Refresh the users list to reflect the change
        await fetchUsers(usersPagination.skip, usersPagination.limit);
        
        return response.data;
      } else {
        throw new Error(response.data?.detail || 'Failed to authorize user');
      }
    } catch (error) {
      console.error('Error authorizing user:', error);
      toast.error(`Failed to authorize user: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deauthorizeUser = async (userId) => {
    try {
      setIsLoading(true);
      
      const response = await fetchWithAuth(`${API_BASE_URL}/${getApiBasePath()}/toggle-authorization/${userId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.error) {
        toast.success(response.data?.message || 'User deauthorized successfully!');
        
        // Refresh the users list to reflect the change
        await fetchUsers(usersPagination.skip, usersPagination.limit);
        
        return response.data;
      } else {
        throw new Error(response.data?.detail || 'Failed to deauthorize user');
      }
    } catch (error) {
      console.error('Error deauthorizing user:', error);
      toast.error(`Failed to deauthorize user: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const bulkAuthorizeUsers = async (userIds) => {
    try {
      setIsLoading(true);
      
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/bulk-authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userIds),
        credentials: 'include'
      });
      
      if (!response.error) {
        toast.success(response.data?.message || 'Users authorized successfully!');
        
        // Refresh the users list to reflect the changes
        await fetchUsers(usersPagination.skip, usersPagination.limit);
        
        return response.data;
      } else {
        throw new Error(response.data?.detail || 'Failed to bulk authorize users');
      }
    } catch (error) {
      console.error('Error bulk authorizing users:', error);
      toast.error(`Failed to bulk authorize users: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAccessControlStatus = async () => {
    try {
      setIsLoading(true);
      
      const response = await fetchWithAuth(`${API_BASE_URL}/${getApiBasePath()}/access-control-status`);
      
      if (!response.error) {
        return response.data;
      } else {
        throw new Error('Failed to fetch access control status');
      }
    } catch (error) {
      console.error('Error fetching access control status:', error);
      toast.error(`Failed to load access control status: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Cache Management Functions
  const getCacheStats = async () => {
    try {
      setIsLoading(true);
      
      const response = await fetchWithAuth(`${API_BASE_URL}/cache/stats`);
      
      if (!response.error) {
        return response.data;
      } else {
        throw new Error('Failed to fetch cache stats');
      }
    } catch (error) {
      console.error('Error fetching cache stats:', error);
      toast.error(`Failed to load cache stats: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearCache = async (cacheType = 'all') => {
    try {
      setIsLoading(true);
      
      const response = await fetchWithAuth(`${API_BASE_URL}/cache/clear?cache_type=${cacheType}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.error) {
        toast.success(response.data?.message || 'Cache cleared successfully!');
        return response.data;
      } else {
        throw new Error('Failed to clear cache');
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast.error(`Failed to clear cache: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const warmCache = async () => {
    try {
      setIsLoading(true);
      
      const response = await fetchWithAuth(`${API_BASE_URL}/cache/warm`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.error) {
        toast.success(response.data?.message || 'Cache warmed successfully!');
        return response.data;
      } else {
        throw new Error('Failed to warm cache');
      }
    } catch (error) {
      console.error('Error warming cache:', error);
      toast.error(`Failed to warm cache: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Token Renewal System Functions
  const triggerTokenRenewal = async () => {
    try {
      setIsLoading(true);
      
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/trigger-token-renewal`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.error) {
        toast.success(response.data?.message || 'Token renewal triggered successfully!');
        return response.data;
      } else {
        throw new Error('Failed to trigger token renewal');
      }
    } catch (error) {
      console.error('Error triggering token renewal:', error);
      toast.error(`Failed to trigger token renewal: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getTokenRenewalStatus = async () => {
    try {
      setIsLoading(true);
      
      const response = await fetchWithAuth(`${API_BASE_URL}/${getApiBasePath()}/token-renewal-status`);
      
      if (!response.error) {
        return response.data;
      } else {
        throw new Error('Failed to fetch token renewal status');
      }
    } catch (error) {
      console.error('Error fetching token renewal status:', error);
      toast.error(`Failed to load token renewal status: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const testRenewalEmail = async (testEmail) => {
    try {
      setIsLoading(true);
      
      const formData = new FormData();
      formData.append('test_email', testEmail);

      const response = await fetchWithAuth(`${API_BASE_URL}/admin/test-renewal-email`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (!response.error) {
        toast.success(response.data?.message || 'Test email sent successfully!');
        return response.data;
      } else {
        throw new Error('Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(`Failed to send test email: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // User Clear Cache Function (for individual users)
  const clearUserCache = async (cacheType = 'all') => {
    try {
      setIsLoading(true);
      
      const response = await fetchWithAuth(`${API_BASE_URL}/clear-cache?cache_type=${cacheType}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.error) {
        toast.success(response.data?.message || 'User cache cleared successfully!');
        return response.data;
      } else {
        throw new Error('Failed to clear user cache');
      }
    } catch (error) {
      console.error('Error clearing user cache:', error);
      toast.error(`Failed to clear user cache: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // System Health Functions
  const getSystemHealth = async () => {
    try {
      setIsLoading(true);
      const cacheStats = await getCacheStats();
      const accessControlStatus = await fetchAccessControlStatus();
      
      return {
        cache: cacheStats.cache_health,
        access_control: accessControlStatus,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error fetching system health:', error);
      toast.error(`Failed to load system health: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // System Configuration Functions
  const getSystemConfig = async () => {
    try {
      setIsLoading(true);
      
      const response = await fetchWithAuth(`${API_BASE_URL}/${getApiBasePath()}/system-config`);
      
      if (!response.error) {
        return response.data;
      } else {
        throw new Error('Failed to fetch system configuration');
      }
    } catch (error) {
      console.error('Error fetching system config:', error);
      toast.error(`Failed to load system configuration: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateSystemConfig = async (setting, value) => {
    try {
      const formData = new FormData();
      formData.append('setting', setting);
      formData.append('value', value);

      const response = await fetchWithAuth(`${API_BASE_URL}/admin/update-system-config`, {
        method: 'POST',
        body: formData
      });

      if (response.error) {
        throw new Error(response.message || 'Failed to update system config');
      }

      toast.success('System configuration updated successfully');
      return response.data;
    } catch (error) {
      console.error('Error updating system config:', error);
      toast.error(error.message || 'Failed to update system configuration');
      throw error;
    }
  };

  // Setup token management functions
  const fetchUsersWithExpiredSetup = async (page = 1, limit = 20, search = '') => {
    try {
      let url = `${API_BASE_URL}/${getApiBasePath()}/users-with-expired-setup?page=${page}&limit=${limit}`;
      
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await fetchWithAuth(url);

      if (response.error) {
        throw new Error(response.message || 'Failed to fetch users with expired setup');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching users with expired setup:', error);
      toast.error(error.message || 'Failed to fetch users with expired setup');
      throw error;
    }
  };

  const extendSetupToken = async (userId, hours = 24) => {
    try {
      const url = `${API_BASE_URL}/${getApiBasePath()}/extend-setup-token/${userId}?hours=${hours}`;
      
      const response = await fetchWithAuth(url, {
        method: 'POST'
      });

      if (response.error) {
        throw new Error(response.message || 'Failed to extend setup token');
      }

      toast.success(response.data.message || 'Setup token extended successfully');
      return response.data;
    } catch (error) {
      console.error('Error extending setup token:', error);
      toast.error(error.message || 'Failed to extend setup token');
      throw error;
    }
  };

  const bulkExtendSetupTokens = async (userIds, hours = 24) => {
    try {
      const url = `${API_BASE_URL}/${getApiBasePath()}/bulk-extend-setup-tokens?hours=${hours}`;
      
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_ids: userIds })
      });

      if (response.error) {
        throw new Error(response.message || 'Failed to bulk extend setup tokens');
      }

      toast.success(response.data.message || 'Setup tokens extended successfully');
      return response.data;
    } catch (error) {
      console.error('Error bulk extending setup tokens:', error);
      toast.error(error.message || 'Failed to bulk extend setup tokens');
      throw error;
    }
  };

  const fetchSetupTokenStatus = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/${getApiBasePath()}/setup-token-status`);

      if (response.error) {
        throw new Error(response.message || 'Failed to fetch setup token status');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching setup token status:', error);
      toast.error(error.message || 'Failed to fetch setup token status');
      throw error;
    }
  };

  // Combine all the state and functions into a value object
  const value = {
    isAdmin,
    isLoading,
    dashboardStats,
    users,
    subscriptionUsers,
    workoutPlans,
    dietPlans,
    feedback,
    ratings,
    analytics,
    latestSubscriptions,
    subscriptionStats,
    usersPagination,
    setUsersPagination,
    fetchDashboardStats,
    fetchUsers,
    fetchUserById,
    fetchWorkoutPlans,
    fetchDietPlans,
    fetchFeedback,
    fetchRatings,
    fetchAnalytics,
    fetchLatestSubscriptions,
    fetchSubscriptionStats,
    updateUser,
    fetchSubscriptionUsers,
    fetchDailyActiveUsers,
    createUser,
    createSubAdmin,
    setUsers,
    // Authorization management functions
    fetchUnauthorizedUsers,
    authorizeUser,
    deauthorizeUser,
    bulkAuthorizeUsers,
    fetchAccessControlStatus,
    // Cache Management Functions
    getCacheStats,
    clearCache,
    warmCache,
    // Token Renewal System Functions
    triggerTokenRenewal,
    getTokenRenewalStatus,
    testRenewalEmail,
    // User Clear Cache Function (for individual users)
    clearUserCache,
    // System Health Functions
    getSystemHealth,
    // System Configuration Functions
    getSystemConfig,
    updateSystemConfig,
    // Setup token management functions
    fetchUsersWithExpiredSetup,
    extendSetupToken,
    bulkExtendSetupTokens,
    fetchSetupTokenStatus,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
